import { useState } from 'react'
import { AlertOctagon, ShieldOff, UserPlus, Users } from 'lucide-react'
import { write } from '../api'
import { dateTime, words } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  ReasonField,
  Refusal,
} from '../ui'
import { useResource } from '../useResource'
import RoleEffect from '../RoleEffect'
import { useRoleLabels } from '../roleLabels'

// ======================================================================
// PARTIES — THESE WRITES CHANGE WHO CAN SEE THE CASE
// ======================================================================
//
// er_case_parties is the table the ACL reads, so a grant here changes
// access immediately and for every subsequent request. It is the single
// most security-relevant write on the router: a row is the difference
// between a colleague who cannot prove the case exists and one who can
// read what is alleged about a named person.
//
// REFUSALS ARE SURFACED AS THE SERVER WORDED THEM, AND THEY ARE
// SPECIFIC. er_cases/parties.py distinguishes eight causes —
// self_grant, user_not_in_org, already_granted,
// adjudicating_role_conflict, owner_adjudicator_conflict and the three
// shape errors — each with its own message written for the person
// reading it. This file does not restate, summarise or soften any of
// them, and it does not guess: the code stays server-side in the audit
// chain and the message is what comes back.
//
// conflict_note IS NOT ON THIS LIST AND MUST NOT BE. The FACT of a
// declaration is what the handling roles need in order to act on it.
// The note is free text about a party's own position and routinely names
// a third person; nobody consented to that name appearing on a list read
// by everyone with view_all_notes.

export default function Parties({ caseId, items, capabilities, me, onReplace }) {
  // The tenant's own word for each role (task 27). See
  // components/cases/roleLabels.js — applied at render, never sent.
  const { label: roleLabel } = useRoleLabels()
  const [dialog, setDialog] = useState(null)
  const canManage = capabilities.includes('manage_parties')

  const mine = me ? items.find((p) => p.user_id === me && !p.revoked_at) : null

  return (
    <Panel
      eyebrow="Who is on this case"
      title="Parties"
      right={
        <>
          {mine && !mine.conflict_declared_at && (
            <Button
              size="sm"
              icon={AlertOctagon}
              onClick={() => setDialog({ kind: 'conflict', party: mine })}
            >
              Declare a conflict
            </Button>
          )}
          {canManage && (
            <Button size="sm" icon={UserPlus} onClick={() => setDialog({ kind: 'grant' })}>
              Grant access
            </Button>
          )}
        </>
      }
    >
      {items.length === 0 ? (
        <Empty icon={Users} title="Nobody is recorded on this case" />
      ) : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li
              key={p.party_id}
              className={`panel-inset flex items-start justify-between gap-4 px-4 py-3 ${
                p.revoked_at ? 'opacity-60' : ''
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-head text-[13px] font-semibold">
                    {p.user_label || p.user_id}
                  </span>
                  <Chip tone={p.revoked_at ? undefined : 'accent'}>
                    {roleLabel(p.case_role)}
                  </Chip>
                  {p.revoked_at && <Chip tone="amber">access withdrawn</Chip>}
                  {p.conflict_declared_at && (
                    <Chip tone="amber" title={dateTime(p.conflict_declared_at)}>
                      conflict declared
                    </Chip>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px] text-faint">
                  <span>Granted {dateTime(p.granted_at)}</span>
                  {p.revoked_at && <span>Withdrawn {dateTime(p.revoked_at)}</span>}
                  {p.conflict_declared_at && (
                    <span>Conflict declared {dateTime(p.conflict_declared_at)}</span>
                  )}
                </div>
              </div>

              {canManage && !p.revoked_at && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={ShieldOff}
                  onClick={() => setDialog({ kind: 'revoke', party: p })}
                >
                  Withdraw
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === 'grant' && (
        <GrantDialog
          caseId={caseId}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'revoke' && (
        <RevokeDialog
          caseId={caseId}
          party={dialog.party}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'conflict' && (
        <ConflictDialog
          caseId={caseId}
          party={dialog.party}
          onClose={() => setDialog(null)}
        />
      )}
    </Panel>
  )
}

function GrantDialog({ caseId, onClose, onReplace }) {
  // The tenant's own word for each role (task 27). See
  // components/cases/roleLabels.js — applied at render, never sent.
  const { label: roleLabel } = useRoleLabels()

  const [userId, setUserId] = useState('')
  const [role, setRole] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Fetched when the dialog opens, not when the case does. It is one
  // configuration read behind the same step-up gate as everything else
  // here, and it names no case — see the vocabularies route.
  const { data: vocab } = useResource('/vocabularies')
  const roles = vocab?.case_role ?? []

  // WHAT EACH ROLE ACTUALLY GRANTS (task 27). Generated server-side from
  // acl.CAPABILITIES — see components/cases/RoleEffect.jsx for why not a
  // word of it is written here. Fetched alongside the vocabulary, on the
  // same gate, naming no case and no person.
  const { data: roleDoc } = useResource('/case-roles')
  const described = roleDoc?.roles ?? []
  const chosen = described.find((r) => r.key === role) || null

  return (
    <Modal
      open
      onClose={onClose}
      title="Give somebody access to this case"
      subtitle="A party row is access. It is never granted across a tenant boundary, never to yourself, and never as a second adjudicating role."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="space-y-4">
        <Field
          label="User id"
          required
          hint="The person's id on this organisation. There is no directory endpoint on the cases API, so this is entered rather than picked — see the notes accompanying this build."
        >
          <input
            className="field mono"
            autoFocus
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </Field>

        {/* A PICKER SINCE TASK 18, AND STILL NOT A LIST IN THIS FILE.
            The nine case roles come from ck_er_party_case_role, which
            GET /vocabularies reads out of pg_get_constraintdef() per
            request — so this offers exactly what the database permits,
            and hardcoding the nine here would be a copy that rots
            silently against the constraint. That was the reason this
            was free text before the endpoint existed; it is the same
            reason the options below are fetched rather than typed out.

            THE FREE-TEXT FALLBACK STAYS for the case where the fetch
            failed or the constraint could not be read. Losing the
            ability to grant a colleague access because a picker could
            not be populated would be a worse failure than an input box,
            and the refusal still enumerates the legal roles. */}
        <Field
          label="Role on this case"
          required
          hint={
            roles.length
              ? 'What this person is on this case. It decides what they can see.'
              : 'If this is not one of the nine case roles, the refusal will list them.'
          }
        >
          {roles.length ? (
            <select
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">Choose a role…</option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          ) : (
            <input className="field" value={role} onChange={(e) => setRole(e.target.value)} />
          )}
        </Field>
      </div>

      {/* WHAT THE CHOSEN ROLE WILL ACTUALLY GRANT, BEFORE IT IS
          GRANTED. This is the whole of task 27 part B: the capability
          map was the module's access model and nobody using the product
          could see it, so somebody adding a witness had no way to know a
          witness sees their own interview record and nothing else.

          IT APPEARS ONLY ONCE A ROLE IS CHOSEN. Rendering all nine at
          once would be a wall of text nobody reads, and the question
          being answered is "what does THIS one do", asked at the moment
          it is asked. */}
      {chosen && (
        <div className="mt-4 rounded-md border border-line bg-surface-2 px-4 py-3.5">
          <div className="eyebrow mb-2.5">
            What {chosen.label} will be able to see
          </div>
          <RoleEffect description={chosen} compact />
        </div>
      )}

      <div className="mt-4">
        <Notice tone="accent" icon={AlertOctagon} title="What this does">
          From the moment it is saved, that person can open this case. Until
          then they cannot prove it exists.
        </Notice>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!userId.trim() || !role.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(
                await write(`/${caseId}/parties`, {
                  body: { user_id: userId.trim(), case_role: role.trim() },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Grant access
        </Button>
      </div>
    </Modal>
  )
}

function RevokeDialog({ caseId, party, onClose, onReplace }) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Withdraw access for ${party.user_label || party.user_id}`}
      subtitle="The row stays on the case — who used to have access is part of the record. Their next request to this case gets nothing at all."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <ReasonField value={reason} onChange={setReason} autoFocus />
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          busy={busy}
          disabled={!reason.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(
                await write(`/${caseId}/parties/${party.party_id}/revoke`, {
                  body: { reason },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Withdraw access
        </Button>
      </div>
    </Modal>
  )
}

/** DECLARING A CONFLICT IS A STATEMENT ABOUT YOUR OWN POSITION.
 *
 *  Any of the nine case roles may do it, including a witness who holds
 *  only view_own_notes. It does NOT revoke anything — it records that
 *  somebody should look. The note is stored and is deliberately never
 *  returned on the party list, because it routinely names a third
 *  person. */
export function ConflictDialog({ caseId, party, onClose }) {
  // The tenant's own word for each role (task 27). See
  // components/cases/roleLabels.js — applied at render, never sent.
  const { label: roleLabel } = useRoleLabels()
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  if (done) {
    return (
      <Modal open onClose={onClose} title="Conflict declared">
        <p className="text-[12.5px] leading-relaxed text-muted">
          Recorded against your involvement as {roleLabel(done.case_role)} on{' '}
          {dateTime(done.conflict_declared_at)}. Declaring a conflict does not
          remove your access — it puts the fact on the case for somebody to act
          on.
        </p>
        <div className="mt-5 flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Declare a conflict of interest"
      subtitle="About your own involvement. It does not withdraw your access; it records that there may be a reason somebody else should take this on."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <Field
        label="What the conflict is"
        hint="Kept on the case for whoever administers it. It is deliberately not shown on the party list, because a note about your own position usually names somebody else."
      >
        <textarea
          className="field"
          rows={4}
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              setDone(
                await write(`/${caseId}/parties/${party.party_id}/conflict`, {
                  body: { note: note.trim() || null },
                }),
              )
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Declare it
        </Button>
      </div>
    </Modal>
  )
}
